/**
 * El radar de suscripciones.
 *
 * ---------------------------------------------------------------------------
 *  La decisión que gobierna este componente
 * ---------------------------------------------------------------------------
 *
 * **No dice si usas un servicio, porque no puede saberlo.** El sistema sabe lo que le
 * pagas, no lo que haces con ello. Aquí no se lee «no usas Netflix»: se lee «llevas
 * 14 meses pagando esto, al año son tanto, decide tú».
 *
 * La diferencia importa. Una aplicación que afirma saber algo que no puede saber
 * pierde la confianza en cuanto se equivoca una vez, y entonces el usuario deja de
 * mirar también los avisos que sí eran ciertos.
 *
 * Por eso cada aviso enseña **el hecho** (lo que pasó, con sus fechas y sus cifras) y
 * **una sugerencia** que deja la decisión en sus manos.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, Clock, TrendingUp, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { formatCOP } from '@eldescuadre/core';

import { getSubscriptionRadar, type RadarFinding } from '../lib/endpoints';
import { Card, Loading } from './ui';

/** El icono y el color de cada tipo de hallazgo. */
const LOOK: Record<
  RadarFinding['kind'],
  { icon: LucideIcon; tone: string; title: string }
> = {
  STOPPED_CHARGING: {
    icon: AlertTriangle,
    tone: 'text-ed-coral-light',
    title: 'Dejó de cobrarse',
  },
  PRICE_INCREASE: {
    icon: TrendingUp,
    tone: 'text-ed-gold',
    title: 'Subió de precio',
  },
  REPEATEDLY_SKIPPED: {
    icon: Clock,
    tone: 'text-ed-gold',
    title: 'La has estado omitiendo',
  },
  DUPLICATED: {
    icon: TriangleAlert,
    tone: 'text-ed-gold',
    title: 'Varias en el mismo sitio',
  },
  LONG_RUNNING: {
    icon: Clock,
    tone: 'text-ed-text-muted',
    title: 'Lleva mucho tiempo',
  },
};

export function SubscriptionRadar(): React.JSX.Element | null {
  const radar = useQuery({
    queryKey: ['recurring', 'radar'],
    queryFn: getSubscriptionRadar,
  });

  if (radar.isLoading) return <Loading label="Revisando tus suscripciones…" />;

  // Si falla, no se enseña nada: es una ayuda, no algo sin lo que la pantalla no
  // funcione. Un aviso de error aquí solo distraería de lo importante.
  if (radar.isError || !radar.data) return null;

  const { findings, annualImpact, totalAnnual, activeCount } = radar.data;

  // Sin suscripciones no hay nada que vigilar, y decirlo sería ruido.
  if (activeCount === 0) return null;

  // Todo en orden: se dice, y se dice **una vez**. El silencio no tranquiliza porque
  // no se distingue de «no lo he mirado».
  if (findings.length === 0) {
    return (
      <Card className="!border-ed-success/30 !bg-ed-success/5">
        <div className="flex items-start gap-3">
          <Check size={18} className="mt-0.5 shrink-0 text-ed-success" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-medium text-ed-success">El radar no encontró nada raro</p>
            <p className="mt-1 text-xs text-ed-text-muted">
              Tus {activeCount} {activeCount === 1 ? 'suscripción' : 'suscripciones'} al día:
              te cuestan {formatCOP(totalAnnual)} al año, unos {formatCOP(Math.round(totalAnnual / 12))}{' '}
              al mes.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Radar de suscripciones"
      action={
        <span className="text-xs text-ed-text-muted">
          {activeCount} activas · {formatCOP(totalAnnual)} al año
        </span>
      }
    >
      {/* La cifra que resume todo. Es lo que hace ver la magnitud de lo pequeño: cada
          una parece poco, juntas son un arriendo. */}
      <div className="mb-4 rounded-ed border border-ed-gold/30 bg-ed-gold/10 p-3">
        <p className="text-xs uppercase tracking-wide text-ed-gold">
          Lo que suman estos avisos
        </p>
        <p className="ed-money font-display text-2xl font-extrabold text-ed-gold">
          {formatCOP(annualImpact)}
        </p>
        <p className="mt-0.5 text-xs text-ed-text-muted">
          al año · unos {formatCOP(Math.round(annualImpact / 12))} al mes
        </p>
      </div>

      <ul className="space-y-3">
        {findings.map((finding, index) => {
          const look = LOOK[finding.kind];
          const Icon = look.icon;

          return (
            <li
              key={`${finding.kind}-${finding.ruleId}-${index}`}
              className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/30 p-3"
            >
              <div className="flex items-start gap-3">
                <Icon size={17} className={`mt-0.5 shrink-0 ${look.tone}`} aria-hidden="true" />

                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${look.tone}`}>
                    {look.title}
                  </p>
                  <p className="mt-0.5 font-medium">{finding.description}</p>
                  <p className="mt-1 text-sm text-ed-text-silver">{finding.message}</p>

                  <p className="mt-2 flex items-start gap-1.5 text-xs text-ed-text-muted">
                    <ArrowRight size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{finding.suggestion}</span>
                  </p>
                </div>

                <span className="ed-money shrink-0 text-right text-sm font-semibold">
                  {formatCOP(finding.annualImpact)}
                  <span className="block text-[10px] font-normal text-ed-text-muted">al año</span>
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <p className="mt-4 border-t border-ed-bg-border/30 pt-3 text-xs text-ed-text-muted">
        El radar mira lo que le pagas, no lo que haces con ello: no sabe si usas un
        servicio. Te enseña el hecho y la cuenta para que decidas tú.
      </p>
    </Card>
  );
}
